import { Injectable, Injector } from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { AbstractRender } from 'src/app/abstract/render.abstract';
import { WidgetsAbstract } from 'src/app/abstract/widgets/widgets.abstract';
import { Logger } from 'src/app/core/logger';
import { FieldOptionModel } from 'src/app/models/widgets/field-option.model';
import { WidgetsRadioModel } from 'src/app/models/widgets/widgets-radio.model';
import { CloneService } from '../clone/clone.service';
import { Config } from 'src/app/config';
import { CommentService } from 'src/app/services/comment.service';
import { RenderService } from './render.service';

@Injectable({
  providedIn: 'root',
})
export class RenderRadioService extends AbstractRender {
  constructor(private injector: Injector, private logger: Logger, private commentService:CommentService,
    private globalRender: RenderService) {
    super();
  }

  /**
   * Recursive render method
   *
   * @param radio A radio node
   * @param config Formly field config array
   * @param widgetRoot Root of the layout
   * @param formlyRoot Root of the FormlyFieldConfig
   * @param isInsideQuestion True if we are rendering a subtree inside a question
   * @param isRecursive True if we want to traverse the tree to the bottom. False if we just want to render a node.
   */
  render(
    widget: WidgetsAbstract,
    config: FormlyFieldConfig[],
    widgetRoot: WidgetsAbstract,
    formlyRoot: FormlyFieldConfig,
    isInsideQuestion: boolean,
    isRecursive: boolean
  ) {
    const radio = widget as WidgetsRadioModel;

    // A checkbox primitive has a label (optional), a multicheckbox and an other input (optional)
    let fieldGroupPrimitive: FormlyFieldConfig = {};
    let fieldRadio: FormlyFieldConfig = {
      key: radio.key,
    };
    const fieldLabel: FormlyFieldConfig = {};
    let fieldOtherInput: FormlyFieldConfig = {};
    let fieldCommentInput: FormlyFieldConfig = {};
    let fieldRadioType: FormlyFieldConfig = {};
    let fieldRadioKeyArray : FormlyFieldConfig = {};

    fieldLabel.props = {};

    // Mix incoming template with the group
    if (radio.template) {
      const modelTemplate = JSON.parse(radio.template);
      // Needed to apply templete expressions on group level, example on labels of radio/input
      //TODO: Can be removed in the future 
      fieldGroupPrimitive = Object.assign({}, fieldGroupPrimitive, modelTemplate);
      // fieldRadio = Object.assign({}, fieldRadio, modelTemplate);
    }

    if (radio.fieldTemplate) {
      const fieldTemplate = JSON.parse(radio.fieldTemplate);
      fieldRadio = Object.assign({}, fieldRadio, fieldTemplate);
    }

    // Mix created input with widget template
    if (radio.template) {
      const modelTemplate = JSON.parse(radio.template);
      fieldRadio = { ...modelTemplate, ...fieldRadio };
      // Deep merge not working... Merge props manually
      const mergedprops = {
        ...fieldRadio.props,
        ...modelTemplate.props,
      };
      fieldRadio.props = mergedprops;
    }

    // 1. Label Field

    // Override labels on Fields
    // Go up to custom model and overrides labels
    const labelOverrides: Map<string, string> = new Map<string, string>();
    CloneService.getLabelOverrides(radio, widgetRoot, labelOverrides);
    const overridedString = CloneService.overrideText(
      radio.label,
      labelOverrides
    );

    if (radio.label) {
      fieldLabel.template = '<div>' + overridedString + '</div>';
    }

    // 2. Radio Field
    fieldRadio.type = 'uw-radio';
    //fieldRadio.wrappers = ['uw-radio'],
      fieldRadio.props = {};

    // If options are provided, use them. If not, use sample data
    // model instanceof WidgetsRadioModel
    if (radio.options && radio.options.length > 0) {
      fieldRadio.props.options = radio.options;
      // Check other Flag
      if (radio.otherFlag) {
        const tempOtherValue = this.getOtherValue(radio);
        fieldRadio.props.options = fieldRadio.props.options?.filter((obj) => obj.label !== 'Other');
        fieldRadio.props.options.push({
          value: tempOtherValue,
          label: radio.otherLabel ? radio.otherLabel : Config.CONSTANTS.otherKeySuffix,
          selected: false,
          disabled: false
        });
      }

      // TODO: Set default value pending
      // Get the last model.option.selected and add field.defaultValue = to its value
      // Use native for; faster than some... https://stackoverflow.com/questions/13843972/angular-js-break-foreach
      for (let i = radio.options.length - 1; i >= 0; i--) {
        const opt: FieldOptionModel = radio.options[i];
        if (opt.selected) {
          fieldRadio.defaultValue = opt.value;
          break;
        }
      }
    } else {
      fieldRadio.props = {
        options: [
          { value: 1, label: 'Yes' },
          { value: 2, label: 'No' },
          { value: 3, label: 'N/A', disabled: true },
        ],
      };
    }

    /* Override validations - from template
      TODO: override parameters
      Example for data validation:
            "data": {
              "condition": "$comparator",
              "left": "$leftField",
              "right": "$rightField" }
      */
    for (const validation of radio.children) {
      try {
        const valTemplate: string = validation.getTemplate();
        if (valTemplate) {
          const template = JSON.parse(valTemplate);

          // FIXME: Check if validation is only for field or for form...
          if (template.name === 'date' || template.name === 'timeRange') {
            // else if it is for whole form...
            const validators = { validators: { validation: [template] } };

            // FIXME: For now apply at the root level for date validation
            formlyRoot.validators = { validation: [template] };
            // rootValidation.push( template );
          } else {
            // Else apply at the field level
            fieldRadio.validators = {
              validation: [template],
            };
          }
        }
      } catch (err) {
        this.logger.warn('validation exeception:' + err);
      }
    }

    if (radio.description) {
      fieldLabel.template += '<div class="prop-radio-description">' + radio.description + '</div>';
    }

    if (radio.required) {
      fieldRadio.props.required = radio.required;
    }

    if (radio.otherFlag) {
      const tempOtherValue = this.getOtherValue(radio);
      fieldOtherInput = {
        key: radio.otherKey ? radio.otherKey : radio.key + Config.CONSTANTS.otherDescription,
        type: 'uw-input',
        validators: { validation: ['trimValidation'] },
        expressions: {
          // hide: '((model && model.'+radio.key+' == "'+ Config.CONSTANTS.otherKeySuffix+'") ? false : true)'
          hide: '((model && model.' + radio.key + ' === "' + tempOtherValue + '") ? false : true)'
        },
        props: {
          label: 'Others',
          maxLength: Config.CONSTANTS.otherMaxLength,
          required: true,
        }
      };
    }

    if (radio.commentDialogOn) {
      fieldRadio.props.change = (field, $event) => {
        if (radio.commentDialogOn && radio.commentDialogOn === $event.value) {
          this.commentService.sendValue(true, "" + radio.parentId);
        } else if (radio.commentDialogOn && radio.commentDialogOn !== $event.value) {
          this.commentService.sendValue(false, "" + radio.parentId);
        } else {
          this.commentService.sendValue(true, "" + radio.parentId);
        }
      };
    }

    if (radio.commentKey) {
      fieldRadio.props.commentKey = radio.commentKey;
    }

    if (radio.commentFieldOn) {
      const temp_array = radio.commentFieldOn.split(';');
      let temp = '';
      for (let i = 0; i < temp_array.length; i++) {
        temp += '(model && model.' + radio.key + ' == "' + temp_array[i] + '") ';
        if (i != temp_array.length - 1) {
          temp += ' || '
        }
      }
      fieldCommentInput = {
        key: radio.commentKey ? radio.commentKey : radio.key + Config.CONSTANTS.commentKeySuffix,
        type: 'uw-input',
        validators: { validation: ['trimValidation'] },
        expressions: {
          hide: '(' + temp + ' ? false : true)'
        },
        props: {
          label: 'Comment',
          maxLength: Config.CONSTANTS.commentMaxLength,
          required: (radio.commentFieldOnOptional !== undefined &&
            radio.commentFieldOnOptional === true) ? false : true,
        }
      };
    }
    // fieldRadio.props!.description = radio.description;

    // 4. Prepare groups
    fieldGroupPrimitive.fieldGroupClassName = 'primitive-radio';
    fieldGroupPrimitive.fieldGroup = [];

    // 5. Render
    fieldLabel.className = 'prop-label';
    fieldGroupPrimitive.fieldGroup?.push(fieldLabel);
    fieldRadio.className = 'prop-radio';
    fieldGroupPrimitive.fieldGroup?.push(fieldRadio);

    if (radio.otherFlag) {
      fieldOtherInput.className = 'prop-other-input';
      fieldGroupPrimitive.fieldGroup?.push(fieldOtherInput);
    }

    if (radio.commentFieldOn) {
      fieldCommentInput.className = 'prop-other-input';
      fieldGroupPrimitive.fieldGroup?.push(fieldCommentInput);
      fieldGroupPrimitive.fieldGroup?.push(this.renderFieldTitle(radio));
      fieldGroupPrimitive.fieldGroup?.push(this.renderFieldCommentId(radio));
    }  
    
    // 6. Option Key addition logic for XML generation for backend for API customers
    //    Find out if radio option values contain complete datapoint names (R2) or suffixes (R1)
    let allOptionValues: string[] = [];
    let isR1 = false;    
    // Add isExtended support. If isExtended is provided, the radio type will be honored (R1 or R2). Period.
    if (radio.isExtended === undefined || radio.isExtended === null) {
      // Implicit logic, using isRadioOptionValueComplete
      isR1 = true;

      // First pass: decide if options are complete or not
      radio.options?.forEach((option, index) => {
        // Implicit logic to check for option values as complete datapoint names
        // - R1 radio type: The option value is just a suffix
        // - R2 radio type: The option value is the complete datapoint name
        if (this.isRadioOptionValueComplete(radio.key, option.value.toString())) {
          isR1 = false;
        }
      });
    } else if (radio.isExtended === true) {
      isR1 = false;
    } else if (radio.isExtended === false) {
      isR1 = true;
    }

    if (isR1) {
      fieldRadioType = {
        key: radio.key + "_FBPT",
        defaultValue: "R1"
      };
    } else {
      fieldRadioType = {
        key: radio.key + "_FBPT",
        defaultValue: "R2"
      };
    }    

    // 7. Collect Option array (fieldRadioDataPoints) in a Second pass, now that we know if keys are complete/suffixes
    radio.options?.forEach((option, index) => {
      allOptionValues.push(option.value.toString());
      let fieldRadioDataPoints: FormlyFieldConfig = {};

      if (isR1) {
        fieldRadioDataPoints = {
          // TODO: Will have to take care of complete option Value        
          key: radio.key + option.value.toString(),
          defaultValue: false
        };

      } else {
        fieldRadioDataPoints = {
          // When complete option Value        
          key: option.value.toString(),
          defaultValue: false
        };
      }
      fieldGroupPrimitive.fieldGroup?.push(fieldRadioDataPoints);
    });    

    // 8. Adding all the values in to an array for backend Processing    
    if(fieldRadioType.defaultValue === "R2"){
      fieldRadioKeyArray = {
        key: radio.key + "_FOPT",
        defaultValue: allOptionValues
      }
      fieldGroupPrimitive.fieldGroup?.push(fieldRadioKeyArray);
    }

    fieldGroupPrimitive.fieldGroup?.push(fieldRadioType);

    // Wrapper For Formly Group
    fieldGroupPrimitive.wrappers = ['field'];
    config.push(fieldGroupPrimitive);
  }

  /**
   * Method to check if radio option values are the complete datapoint name
   * We use statistical approach: If the radio key stems are included in some of the
   * option values, there's a good probability that the option is a complete key
   * Example:
   * - For radio key "isWallConcreteIndicator" (stems is, wall, concrete, indicator) 
   *   and option value "Yes", probability of being complete is 0%
   * - For radio key "isWallMaterialIndicator" (stems = is, wall, material, indicator) 
   *   and option value "wallMaterialIndicatorMetal", probability of being complete is 3/5 = 60%
   * @param key 
   * @param value 
   * @returns true if option values are complete datapoint names
   */
  isRadioOptionValueComplete(key: string, value: string): boolean {
    const keyWords = key.split(/(?=[A-Z])/); // Split the key into parts
  
    // Check if words in key exist in the value   
    let numberOfWordsInValue = 0;
    let keyWordLength = keyWords.length;
    keyWords.forEach(word =>{
      if (value.includes(word)){
        numberOfWordsInValue = + 1;
      }
    });    

    // Simple math probability
    if (numberOfWordsInValue/keyWordLength*100 >= 50){
      return true;
    } else {
      return false;
    }    
  }

  getOtherValue(radio: WidgetsRadioModel): string {
    return radio.otherKey ? radio.otherKey : Config.CONSTANTS.otherKeySuffix;
  }

  renderFieldTitle(radio: any) {
    // 2. Question Title
    // "ParkingLotCoveredIndicator_FBQTI": "Question title",
    let radioCommentKey: FormlyFieldConfig;
    if (radio.label) {      
      radioCommentKey = {
        key: radio.key + Config.CONSTANTS.commentKeyTitle,
        defaultValue: radio.label       
      }
    } else {
      console.log();
      radioCommentKey = {
        key: radio.key + Config.CONSTANTS.commentKeyTitle,
        expressions: {
          defaultValue: `field.formControl?.setValue(field.parent?.parent?.props?.label)`
        }       
      }      
    }
    return radioCommentKey;
  }

  renderFieldCommentId(radio: any) {
    // 3. Comment id
    // "ParkingLotCoveredIndicator_FBCID": "c109_comment",
    let radioCommentKey: FormlyFieldConfig;

    radioCommentKey = {
      key: radio.key + Config.CONSTANTS.commentKeyId,
      defaultValue: 'c'+ this.globalRender.getCounter() + Config.CONSTANTS.commentSuffix      
    }
    return radioCommentKey;
  }
}
